// plbx REST client for the Playbox Excalidraw API.
// All calls use cookie credentials (oauth2-proxy) — no bearer needed in browser.
// See spec/SPEC.md §REST API contract.

const API_URL =
  import.meta.env.VITE_APP_API_URL || "https://api.draw.plbx.ai/api/v1";

export type Workspace = {
  id: string;
  slug: string;
  name: string;
  kind: "shared" | "personal";
  sceneCount: number;
};

export type Scene = {
  id: string;
  name: string;
  pinned: boolean;
  workspaceId: string;
  workspaceSlug: string | null;
  ownerEmail: string;
  collectionId: string | null;
  sceneVersion: number;
  createdAt: string;
  updatedAt: string;
};

export type Paginated<T> = {
  data: T[];
  offset: number;
  limit: number;
  hasNextPage: boolean;
};

export type SceneContent = {
  type: string;
  version: number;
  source: string;
  sceneVersion: number;
  elements: any[];
  appState: Record<string, any>;
  files: Record<string, any>;
};

export type ApiKey = {
  id: string;
  name: string;
  prefix: string;
  workspaceId: string | null;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
};

export type CreatedApiKey = {
  id: string;
  name: string;
  token: string; // only returned once on creation
};

class PlbxApiError extends Error {
  constructor(
    public statusCode: number,
    public errorCode: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { keepalive?: boolean },
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: "include",
    // keepalive lets a PUT outlive page teardown (visibilitychange/pagehide
    // flush on close). Browser cap: ~64KB total body across inflight keepalive
    // requests — fine for vector boards, may drop oversized image scenes.
    keepalive: opts?.keepalive,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let payload: any = {};
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    throw new PlbxApiError(
      res.status,
      payload.error || "error",
      payload.message || res.statusText,
    );
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

// ---------------- workspaces ----------------

export const listWorkspaces = () =>
  request<Paginated<Workspace>>("GET", "/workspaces");

export const getWorkspace = (id: string) =>
  request<Workspace>("GET", `/workspaces/${encodeURIComponent(id)}`);

// ---------------- scenes ----------------

export const listScenes = (
  opts: {
    offset?: number;
    limit?: number;
    collectionId?: string;
    workspaceId?: string;
  } = {},
) => {
  const qs = new URLSearchParams();
  if (opts.offset != null) qs.set("offset", String(opts.offset));
  if (opts.limit != null) qs.set("limit", String(opts.limit));
  if (opts.collectionId) qs.set("collectionId", opts.collectionId);
  if (opts.workspaceId) qs.set("workspaceId", opts.workspaceId);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<Paginated<Scene>>("GET", `/scenes${suffix}`);
};

export const createScene = (body: {
  name: string;
  workspaceId: string;
  pinned?: boolean;
  collectionId?: string;
}) => request<Scene>("POST", "/scenes", body);

export const getScene = (id: string) =>
  request<Scene>("GET", `/scenes/${encodeURIComponent(id)}`);

export const patchScene = (
  id: string,
  body: {
    name?: string;
    pinned?: boolean;
    collectionId?: string | null;
    workspaceId?: string;
  },
) => request<Scene>("PATCH", `/scenes/${encodeURIComponent(id)}`, body);

export const deleteScene = (id: string) =>
  request<void>("DELETE", `/scenes/${encodeURIComponent(id)}`);

export const getSceneContent = (id: string) =>
  request<SceneContent>(
    "GET",
    `/scenes/${encodeURIComponent(id)}/content`,
  );

export const putSceneContent = (
  id: string,
  body: {
    type: string;
    version: number;
    source: string;
    elements: readonly any[];
    appState: Record<string, any>;
    files: Record<string, any>;
  },
  opts?: { keepalive?: boolean },
) =>
  request<Scene & { sceneVersion: number }>(
    "PUT",
    `/scenes/${encodeURIComponent(id)}/content`,
    body,
    opts,
  );

// ---------------- api keys ----------------

export const listApiKeys = () =>
  request<{ data: ApiKey[] }>("GET", "/account/api-keys");

export const createApiKey = (body: {
  name: string;
  workspaceId?: string | null;
  scopes?: string[];
  expires_at?: string | null;
}) => request<CreatedApiKey>("POST", "/account/api-keys", body);

export const revokeApiKey = (id: string) =>
  request<void>(
    "DELETE",
    `/account/api-keys/${encodeURIComponent(id)}`,
  );

// ---------------- duplicate (client-side helper) ----------------

/**
 * Duplicate a scene by fetching its content and POSTing a new scene with the
 * same elements/files. Server-side has no /duplicate endpoint — done in two
 * round-trips. Name suffixed with " (copy)".
 */
export const duplicateScene = async (
  src: Scene,
  workspaceId: string = src.workspaceId,
): Promise<Scene> => {
  const content = await getSceneContent(src.id);
  const fresh = await createScene({
    name: `${src.name} (copy)`,
    pinned: false,
    workspaceId,
  });
  await putSceneContent(fresh.id, {
    type: content.type,
    version: content.version,
    source: content.source,
    elements: content.elements,
    appState: content.appState,
    files: content.files,
  });
  return fresh;
};

// ---------------- share ----------------

export type ShareLink = { readOnlyUrl: string };

export const createShareLink = (id: string) =>
  request<ShareLink>("POST", `/scenes/${encodeURIComponent(id)}/share`);

export type SharedScene = {
  id: string;
  name: string;
  elements: any[];
  appState: Record<string, any>;
  files: Record<string, any>;
};

export const getSharedScene = async (token: string): Promise<SharedScene> => {
  const res = await fetch(
    `${API_URL}/share/${encodeURIComponent(token)}`,
    { credentials: "omit" },
  );
  if (!res.ok) {
    throw new PlbxApiError(res.status, "share_error", res.statusText);
  }
  return (await res.json()) as SharedScene;
};

export { PlbxApiError, API_URL };
